import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Icon,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Grid,
  GridItem,
  Link,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  CircularProgress,
  CircularProgressLabel,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiCalendar,
  FiDollarSign,
  FiBarChart2,
  FiShield,
  FiInfo,
  FiExternalLink,
  FiChevronLeft,
  FiHome,
  FiAward,
  FiPercent,
  FiClock,
  FiLayers,
  FiGlobe,
  FiUser,
  FiBookOpen,
  FiCheckCircle,
  FiAlertTriangle,
  FiActivity,
  FiPieChart,
  FiHelpCircle,
  FiLogIn,
  FiCreditCard,
  FiCheck,
} from "react-icons/fi";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { useFunds } from "../../hooks/useFunds";
import { useFundInvestNavigation } from "../../hooks/useFundInvestNavigation";
import type {
  PublicStaticFund,
  StaticFundContent,
} from "../../data/publicStaticFunds";
import { getPublicStaticFundBySlug } from "../../data/publicStaticFunds";
import {
  getFundLogoSrc,
  getFundRedemptionNavLabel,
  getFundSubscriptionNavLabel,
  isFundInvestmentEnabled,
} from "./fundCardUtils";

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
);

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

// ─── Helpers ───────────────────────────────────────────────

const formatNumber = (num: number): string =>
  new Intl.NumberFormat("fa-IR").format(num);

const formatCurrency = (num: number): string => {
  if (num >= 1_000_000_000_000)
    return `${(num / 1_000_000_000_000).toFixed(1)} هزار میلیارد`;
  if (num >= 1_000_000_000)
    return `${(num / 1_000_000_000).toFixed(1)} میلیارد`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)} میلیون`;
  return formatNumber(num);
};

const formatPercent = (val: number): string => val.toFixed(2);

const formatDate = (dateStr: string): string => {
  try {
    const d = new Date(dateStr);
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
  } catch {
    return dateStr;
  }
};

const getFundTypeColor = (type: string): string => {
  if (type.includes("سهامی")) return "red";
  if (type.includes("درآمد ثابت")) return "green";
  if (type.includes("مختلط")) return "purple";
  if (type.includes("کالایی")) return "orange";
  return "gray";
};

const FundDetailSkeleton = () => {
  const bgColor = useColorModeValue("white", "dark.900");
  const cardBg = useColorModeValue("white", "dark.50");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const heroBgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249, 168, 166, 0.5), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249, 168, 166, 0.5), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173, 11, 6, 0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173, 11, 6, 0.22), transparent 50%)",
  );

  return (
    <>
      <Box
        position="relative"
        bg={bgColor}
        overflow="hidden"
        pt={{ base: "100px", md: "90px" }}
        pb={{ base: 10, md: 16 }}
        _before={{
          content: '""',
          position: "absolute",
          inset: 0,
          background: heroBgGradient,
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <Skeleton height="18px" width="220px" mb={6} borderRadius="md" />

          <VStack align="stretch" spacing={5}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              gap={4}
            >
              <VStack align="start" spacing={3} flex={1}>
                <Skeleton
                  height="42px"
                  width={{ base: "75%", md: "420px" }}
                  borderRadius="lg"
                />
                <Skeleton
                  height="20px"
                  width={{ base: "55%", md: "260px" }}
                  borderRadius="md"
                />
              </VStack>

              <HStack spacing={3}>
                <Skeleton height="44px" width="150px" borderRadius="xl" />
                <Skeleton height="44px" width="150px" borderRadius="xl" />
              </HStack>
            </Flex>

            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={4}
              w="100%"
              mt={4}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                p={5}
              >
                <SkeletonText noOfLines={4} spacing={4} />
              </Box>
              <Box
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                p={5}
              >
                <SkeletonText noOfLines={4} spacing={4} />
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={{ base: 10, md: 14 }}>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={8}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Box
              key={index}
              bg={cardBg}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              p={6}
            >
              <HStack mb={4} spacing={3}>
                <SkeletonCircle size="10" />
                <Skeleton height="24px" flex="1" borderRadius="md" />
              </HStack>
              <SkeletonText noOfLines={6} spacing={4} />
            </Box>
          ))}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Box
              key={index}
              bg={cardBg}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              p={6}
            >
              <Skeleton height="28px" width="220px" mb={5} borderRadius="md" />
              <SkeletonText noOfLines={8} spacing={4} />
            </Box>
          ))}
        </SimpleGrid>
      </Container>
    </>
  );
};

// ─── Component ─────────────────────────────────────────────

interface FundDetailProps {
  fundOverride?: PublicStaticFund;
}

export const FundDetail = ({ fundOverride }: FundDetailProps) => {
  const { fundId } = useParams<{ fundId: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useFunds();
  const navigateToInvest = useFundInvestNavigation();
  const matchedStaticFund = getPublicStaticFundBySlug(data?.items || [], fundId);
  const fund =
    fundOverride ||
    matchedStaticFund ||
    data?.items.find((f) => f.fundRegisterCode === fundId);
  const subscriptionNavLabel = getFundSubscriptionNavLabel(
    fund?.fundRegisterCode,
  );
  const redemptionNavLabel = getFundRedemptionNavLabel(fund?.fundRegisterCode);
  const staticContent: StaticFundContent | undefined =
    fundOverride?.staticContent || matchedStaticFund?.staticContent;
  const isStaticFund = Boolean(staticContent);

  // ── Color tokens ──
  const bgColor = useColorModeValue("white", "dark.900");
  const sectionBg = useColorModeValue("gray.50", "dark.900");
  const cardBg = useColorModeValue("white", "dark.50");
  const textColor = useColorModeValue("gray.800", "white");
  const subtitleColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const statBg = useColorModeValue("gray.50", "dark.900");
  const returnPositiveBg = useColorModeValue(
    "green.50",
    "rgba(74,222,128,0.08)",
  );
  const returnNegativeBg = useColorModeValue(
    "red.50",
    "rgba(248,113,113,0.08)",
  );
  const heroBgGradient = useColorModeValue(
    "radial-gradient(circle at 85% 45%, rgba(249, 168, 166, 0.5), transparent 50%), radial-gradient(circle at 15% 85%, rgba(249, 168, 166, 0.5), transparent 50%)",
    "radial-gradient(circle at 85% 15%, rgba(173, 11, 6, 0.22), transparent 50%), radial-gradient(circle at 15% 85%, rgba(173, 11, 6, 0.22), transparent 50%)",
  );
  const ctaBgGradient = useColorModeValue(
    "linear(135deg, brand.500, brand.700)",
    "linear(135deg, brand.600, brand.900)",
  );
  const progressTrackColor = useColorModeValue("gray.100", "gray.700");

  if (!fundOverride && isLoading) {
    return <FundDetailSkeleton />;
  }

  // ── 404 ──
  if (!fund) {
    return (
      <Box py={40} textAlign="center">
        <Container maxW="container.md">
          <VStack spacing={6}>
            <Heading color={textColor} fontSize="2xl">
              صندوق مورد نظر یافت نشد
            </Heading>
            <Text color={subtitleColor}>
              کد صندوق وارد شده معتبر نیست یا اطلاعاتی برای آن موجود نمی‌باشد.
            </Text>
            <Button
              colorScheme="brand"
              onClick={() => navigate("/funds")}
              rightIcon={
                <Icon verticalAlign="middle" as={FiArrowLeft as any} />
              }
            >
              بازگشت به لیست صندوق‌ها
            </Button>
          </VStack>
        </Container>
      </Box>
    );
  }

  // ── Derived data ──
  const penalties = [];
  for (let i = 1; i <= 5; i++) {
    const pct = fund.wageSettings.find((w) => w.name === `Penalty${i}Percent`);
    const day = fund.wageSettings.find((w) => w.name === `Penalty${i}ToDay`);
    if (pct && day && (Number(pct.value) > 0 || Number(day.value) > 0)) {
      penalties.push({
        percent: pct.value,
        days: day.value,
        description: pct.description,
      });
    }
  }

  const issuanceFixedWage =
    fund.wageSettings.find((w) => w.name === "IssuanceFixedWage")?.value || "0";
  const redemptionFixedWage =
    fund.wageSettings.find((w) => w.name === "RedemptionFixedWage")?.value ||
    "0";
  const issuanceVarRate =
    fund.wageSettings.find((w) => w.name === "IssuanceVariableWageRate")
      ?.value || "0";
  const issuanceMaxVar =
    fund.wageSettings.find((w) => w.name === "IssuanceMaxVariableWage")
      ?.value || "0";
  const fundLogoSrc = getFundLogoSrc(fund.fundRegisterCode);
  const isInvestmentDisabled = !isFundInvestmentEnabled(fund.fundRegisterCode);
  const websiteHref = staticContent?.websiteUrl
    ? staticContent.websiteUrl
    : fund.websiteAddress
      ? `https://${fund.websiteAddress}`
      : undefined;
  const handleInvestClick = () => {
    if (isInvestmentDisabled) {
      return;
    }

    navigateToInvest(fund.fundRegisterCode);
  };

  const returnPeriods = [
    { label: "روزانه", value: fund.dailyReturn, icon: FiClock },
    { label: "هفتگی", value: fund.weeklyReturn, icon: FiCalendar },
    { label: "ماهانه", value: fund.monthlyReturn, icon: FiCalendar },
    { label: "سه‌ماهه", value: fund.quarterReturn, icon: FiCalendar },
    { label: "شش‌ماهه", value: fund.semiYearly, icon: FiCalendar },
    { label: "سالانه", value: fund.annualReturn, icon: FiTrendingUp },
    { label: "از ابتدا", value: fund.overallReturn, icon: FiBarChart2 },
  ];

  // ── Risk Score (derived from fund type) ──
  const riskScore = fund.fundTypeTitle.includes("درآمد ثابت")
    ? 20
    : fund.fundTypeTitle.includes("سهامی")
      ? 65
      : fund.fundTypeTitle.includes("مختلط")
        ? 45
        : 50;

  // ── Asset Composition (mock data based on fund type) ──
  const isFixedIncome = fund.fundTypeTitle.includes("درآمد ثابت");
  const assetCompositionItems = isFixedIncome
    ? [
        { label: "اوراق بدهی", value: 55, color: "#ad0b06" },
        { label: "سپرده بانکی", value: 25, color: "#e85d58" },
        { label: "سهام", value: 10, color: "#f4a8a6" },
        { label: "گواهی سپرده", value: 7, color: "#d4d4d8" },
        { label: "سایر", value: 3, color: "#a1a1aa" },
      ]
    : [
        { label: "سهام", value: 60, color: "#ad0b06" },
        { label: "اوراق بدهی", value: 15, color: "#e85d58" },
        { label: "سپرده بانکی", value: 10, color: "#f4a8a6" },
        { label: "حق تقدم", value: 10, color: "#d4d4d8" },
        { label: "سایر", value: 5, color: "#a1a1aa" },
      ];

  const assetCompositionData = {
    labels: assetCompositionItems.map((i) => i.label),
    datasets: [
      {
        data: assetCompositionItems.map((i) => i.value),
        backgroundColor: assetCompositionItems.map((i) => i.color),
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  // ── How to Invest Steps ──
  const investSteps = [
    {
      icon: FiLogIn,
      title: "ورود به پنل سرمایه گذار",
      description: "با حساب کاربری خود وارد پنل سرمایه‌گذاری شوید.",
    },
    {
      icon: FiPieChart,
      title: "انتخاب صندوق",
      description: "از بین صندوق‌های موجود، صندوق مورد نظر خود را انتخاب کنید.",
    },
    {
      icon: FiCreditCard,
      title: "وارد کردن مبلغ",
      description: "مبلغ سرمایه‌گذاری را وارد کرده و اطلاعات را بررسی نمایید.",
    },
    {
      icon: FiCheck,
      title: "تایید و پرداخت",
      description:
        "عملیات پرداخت را تایید کنید و منتظر تخصیص واحدهای صندوق باشید.",
    },
  ];

  // ── FAQ Items ──
  const faqItems = staticContent?.faqs ?? [
    {
      question: "حداقل مبلغ سرمایه‌گذاری در این صندوق چقدر است؟",
      answer: `حداقل سرمایه‌گذاری در صندوق ${fund.fundName.trim()} معادل ${formatNumber(fund.minimumCapitalUnit)} واحد است. مبلغ ریالی بر اساس NAV صدور روز محاسبه می‌شود.`,
    },
    {
      question: "نحوه ابطال واحدهای سرمایه‌گذاری چگونه است؟",
      answer:
        "برای ابطال واحدها، از طریق پنل سرمایه گذار سرمایه‌گذاری درخواست ابطال ثبت کنید. وجه ابطال‌شده معمولاً ظرف ۲ تا ۵ روز کاری به حساب شما واریز می‌شود.",
    },
    {
      question: "سود صندوق چگونه و چه زمانی پرداخت می‌شود؟",
      answer:
        fund.profitDay > 0
          ? `سود این صندوق به صورت ماهانه در روز ${fund.profitDay} هر ماه به حساب سرمایه‌گذاران واریز می‌شود.`
          : "سود این صندوق در قالب افزایش ارزش خالص دارایی (NAV) لحاظ می‌شود و در زمان ابطال واحدها قابل دریافت است.",
    },
    {
      question: "آیا اصل سرمایه من تضمین شده است؟",
      answer:
        fund.guaranteeRate > 0
          ? `بله، این صندوق دارای ضمانت سودآوری با نرخ ${fund.guaranteeRate}% است. ضامن نقدشوندگی صندوق ${fund.liquidityGurantor1} می‌باشد.`
          : `این صندوق دارای ضامن نقدشوندگی (${fund.liquidityGurantor1}) است که نقدشوندگی واحدها را تضمین می‌کند. با این حال، سرمایه‌گذاری در این صندوق ممکن است با نوسان همراه باشد.`,
    },
    {
      question: "تفاوت NAV صدور و ابطال چیست؟",
      answer:
        "NAV صدور قیمتی است که شما برای خرید واحد صندوق پرداخت می‌کنید و NAV ابطال قیمتی است که در زمان فروش واحد دریافت می‌کنید. تفاوت این دو شامل کارمزدها و هزینه‌های صندوق است.",
    },
  ];

  if (isStaticFund && staticContent) {
    return (
      <>
        <Box
          position="relative"
          bg={bgColor}
          overflow="hidden"
          pt={{ base: "100px", md: "90px" }}
          pb={{ base: 10, md: 16 }}
          _before={{
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: heroBgGradient,
            zIndex: 0,
          }}
        >
          <Container maxW="container.xl" position="relative" zIndex={1}>
            <MotionBox
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              mb={6}
            >
              <Breadcrumb
                separator={
                  <Icon
                    verticalAlign="middle"
                    as={FiChevronLeft as any}
                    color={subtitleColor}
                    boxSize={3}
                  />
                }
                fontSize="sm"
                color={subtitleColor}
              >
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={RouterLink}
                    to="/"
                    _hover={{ color: "brand.500" }}
                  >
                    <HStack spacing={1}>
                      <Icon
                        verticalAlign="middle"
                        as={FiHome as any}
                        boxSize={3.5}
                      />
                      <Text>خانه</Text>
                    </HStack>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    as={RouterLink}
                    to="/funds"
                    _hover={{ color: "brand.500" }}
                  >
                    صندوق‌ها
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem isCurrentPage>
                  <Text color={textColor} fontWeight="medium">
                    {fund.fundName.trim()}
                  </Text>
                </BreadcrumbItem>
              </Breadcrumb>
            </MotionBox>

            <MotionVStack
              spacing={5}
              align="start"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "start", md: "center" }}
                w="100%"
                gap={4}
              >
                <HStack align="start" spacing={4}>
                  <Box
                    w="80px"
                    h="80px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                    flexShrink={0}
                  >
                    {fundLogoSrc ? (
                      <Image
                        src={fundLogoSrc}
                        alt={fund.fundName.trim()}
                        maxW="100%"
                        maxH="100%"
                        objectFit="contain"
                      />
                    ) : (
                      <Text fontWeight="bold" color={subtitleColor}>
                        {fund.fundRegisterCode}
                      </Text>
                    )}
                  </Box>

                  <VStack align="start" spacing={2}>
                    <HStack spacing={3} flexWrap="wrap">
                      <Heading
                        as="h1"
                        fontSize={{ base: "xl", md: "3xl", lg: "4xl" }}
                        fontWeight="extrabold"
                        color={textColor}
                        lineHeight="short"
                      >
                        {fund.fundName.trim()}
                      </Heading>
                      <Badge
                        colorScheme={getFundTypeColor(fund.fundTypeTitle)}
                        fontSize={{ base: "xs", md: "sm" }}
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        {fund.fundTypeTitle.trim()}
                      </Badge>
                    </HStack>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color={subtitleColor}
                    >
                      {staticContent.heroSubtitle}
                    </Text>
                  </VStack>
                </HStack>

                <HStack spacing={3} flexShrink={0}>
                  <Button
                    colorScheme="brand"
                    size={{ base: "md", md: "lg" }}
                    rightIcon={
                      <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                    }
                    onClick={handleInvestClick}
                    isDisabled={isInvestmentDisabled}
                    shadow="lg"
                    _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                    transition="all 0.3s"
                  >
                    سرمایه‌گذاری
                  </Button>
                  {websiteHref && (
                    <Button
                      variant="outline"
                      colorScheme="brand"
                      size={{ base: "md", md: "lg" }}
                      rightIcon={
                        <Icon
                          verticalAlign="middle"
                          as={FiExternalLink as any}
                        />
                      }
                      as="a"
                      href={websiteHref}
                      target="_blank"
                    >
                      وبسایت صندوق
                    </Button>
                  )}
                </HStack>
              </Flex>

              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={4}
                w="100%"
                mt={4}
              >
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={borderColor}
                  overflow="hidden"
                  shadow="sm"
                >
                  <HStack
                    px={5}
                    py={3}
                    borderBottom="1px solid"
                    borderColor={borderColor}
                    spacing={2}
                  >
                    <Box
                      bg="brand.50"
                      _dark={{ bg: "brand.900" }}
                      borderRadius="lg"
                      p={1.5}
                    >
                      <Icon
                        verticalAlign="middle"
                        as={FiDollarSign as any}
                        color="brand.500"
                        boxSize={4}
                      />
                    </Box>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      قیمت‌های معاملاتی
                    </Text>
                    <Badge
                      colorScheme="brand"
                      variant="subtle"
                      fontSize="2xs"
                      mr="auto"
                    >
                      {staticContent.updatedAt}
                    </Badge>
                  </HStack>
                  <Grid templateColumns="1fr 1fr">
                    <VStack
                      spacing={0.5}
                      py={5}
                      px={4}
                      align="center"
                      borderRight="1px solid"
                      borderColor={borderColor}
                    >
                      <Text
                        fontSize="2xs"
                        color={subtitleColor}
                        letterSpacing="wide"
                      >
                        قیمت آخرین معامله
                      </Text>
                      <Text
                        fontSize={{ base: "lg", md: "xl" }}
                        fontWeight="extrabold"
                        color={textColor}
                      >
                        {staticContent.lastTradePrice}
                      </Text>
                    </VStack>
                    <VStack spacing={0.5} py={5} px={4} align="center">
                      <Text
                        fontSize="2xs"
                        color={subtitleColor}
                        letterSpacing="wide"
                      >
                        قیمت پایانی
                      </Text>
                      <Text
                        fontSize={{ base: "lg", md: "xl" }}
                        fontWeight="extrabold"
                        color={textColor}
                      >
                        {staticContent.closingPrice}
                      </Text>
                    </VStack>
                  </Grid>
                </Box>

                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={borderColor}
                  overflow="hidden"
                  shadow="sm"
                >
                  <HStack
                    px={5}
                    py={3}
                    borderBottom="1px solid"
                    borderColor={borderColor}
                    spacing={2}
                  >
                    <Box
                      bg="brand.50"
                      _dark={{ bg: "brand.900" }}
                      borderRadius="lg"
                      p={1.5}
                    >
                      <Icon
                        verticalAlign="middle"
                        as={FiBarChart2 as any}
                        color="brand.500"
                        boxSize={4}
                      />
                    </Box>
                    <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                      میانگین بازده سالانه
                    </Text>
                  </HStack>
                  <VStack spacing={1} py={6} px={4} align="center">
                    <Text
                      fontSize="2xs"
                      color={subtitleColor}
                      letterSpacing="wide"
                    >
                      بازده سالانه
                    </Text>
                    <Text
                      fontSize={{ base: "2xl", md: "3xl" }}
                      fontWeight="extrabold"
                      color="green.500"
                    >
                      {staticContent.averageAnnualReturn}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={subtitleColor}
                      textAlign="center"
                    >
                      میانگین بازده سالانه صندوق
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </MotionVStack>
          </Container>
        </Box>

        <Box py={{ base: 10, md: 16 }} bg={sectionBg}>
          <Container maxW="container.xl">
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <HStack spacing={2} mb={8}>
                <Icon
                  verticalAlign="middle"
                  as={FiInfo as any}
                  color="brand.500"
                  boxSize={5}
                />
                <Heading
                  as="h2"
                  fontSize={{ base: "lg", md: "xl" }}
                  color={textColor}
                >
                  {staticContent.suitabilityTitle}
                </Heading>
              </HStack>

              <Text color={subtitleColor} mb={8} lineHeight="tall">
                {staticContent.suitabilitySubtitle}
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
                {staticContent.metrics.map((metric) => (
                  <Box
                    key={metric.title}
                    bg={cardBg}
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor={borderColor}
                    shadow="sm"
                  >
                    <Text fontSize="sm" color={subtitleColor} mb={2}>
                      {metric.title}
                    </Text>
                    <Text
                      fontSize={{ base: "xl", md: "2xl" }}
                      fontWeight="extrabold"
                      color={textColor}
                      mb={3}
                    >
                      {metric.value}
                    </Text>
                    <Text fontSize="sm" color={subtitleColor} lineHeight="tall">
                      {metric.helper}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </MotionBox>
          </Container>
        </Box>

        <Box py={{ base: 10, md: 16 }} bg={bgColor}>
          <Container maxW="container.xl">
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <HStack spacing={2} mb={8}>
                <Icon
                  verticalAlign="middle"
                  as={FiHelpCircle as any}
                  color="brand.500"
                  boxSize={5}
                />
                <Heading
                  as="h2"
                  fontSize={{ base: "lg", md: "xl" }}
                  color={textColor}
                >
                  سوالات متداول
                </Heading>
              </HStack>

              <Box
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                overflow="hidden"
              >
                <Accordion allowToggle>
                  {faqItems.map((faq, idx) => (
                    <AccordionItem
                      key={idx}
                      border="none"
                      borderBottom={
                        idx < faqItems.length - 1 ? "1px solid" : "none"
                      }
                      borderColor={borderColor}
                    >
                      <AccordionButton
                        py={5}
                        px={6}
                        _hover={{ bg: statBg }}
                        _expanded={{
                          bg: statBg,
                          borderBottom: "1px solid",
                          borderColor: borderColor,
                        }}
                      >
                        <HStack flex={1} spacing={3} textAlign="right">
                          <Icon
                            verticalAlign="middle"
                            as={FiHelpCircle as any}
                            color="brand.500"
                            boxSize={4}
                            flexShrink={0}
                          />
                          <Text
                            fontWeight="semibold"
                            color={textColor}
                            fontSize="sm"
                          >
                            {faq.question}
                          </Text>
                        </HStack>
                        <AccordionIcon color={subtitleColor} />
                      </AccordionButton>
                      <AccordionPanel px={6} py={5} color={subtitleColor}>
                        <Text fontSize="sm" lineHeight="tall">
                          {faq.answer}
                        </Text>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Box>
            </MotionBox>
          </Container>
        </Box>
      </>
    );
  }

  return (
    <>
      {/* ─── HERO ─── */}
      <Box
        position="relative"
        bg={bgColor}
        overflow="hidden"
        pt={{ base: "100px", md: "90px" }}
        pb={{ base: 10, md: 16 }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: heroBgGradient,
          zIndex: 0,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          {/* Breadcrumb */}
          <MotionBox
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            mb={6}
          >
            <Breadcrumb
              separator={
                <Icon
                  verticalAlign="middle"
                  as={FiChevronLeft as any}
                  color={subtitleColor}
                  boxSize={3}
                />
              }
              fontSize="sm"
              color={subtitleColor}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to="/"
                  _hover={{ color: "brand.500" }}
                >
                  <HStack spacing={1}>
                    <Icon
                      verticalAlign="middle"
                      as={FiHome as any}
                      boxSize={3.5}
                    />
                    <Text>خانه</Text>
                  </HStack>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbLink
                  as={RouterLink}
                  to="/funds"
                  _hover={{ color: "brand.500" }}
                >
                  صندوق‌ها
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <Text color={textColor} fontWeight="medium">
                  {fund.fundName.trim()}
                </Text>
              </BreadcrumbItem>
            </Breadcrumb>
          </MotionBox>

          <MotionVStack
            spacing={5}
            align="start"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "start", md: "center" }}
              w="100%"
              gap={4}
            >
              <HStack align="start" spacing={4}>
                <Box
                  w={"80px"}
                  h={"80px"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                  flexShrink={0}
                >
                  {fundLogoSrc ? (
                    <Image
                      src={fundLogoSrc}
                      alt={fund.fundName.trim()}
                      maxW="100%"
                      maxH="100%"
                      objectFit="contain"
                    />
                  ) : (
                    <Text fontWeight="bold" color={subtitleColor}>
                      {fund.fundRegisterCode}
                    </Text>
                  )}
                </Box>

                <VStack align="start" spacing={2}>
                  <HStack spacing={3} flexWrap="wrap">
                    <Heading
                      as="h1"
                      fontSize={{ base: "xl", md: "3xl", lg: "4xl" }}
                      fontWeight="extrabold"
                      color={textColor}
                      lineHeight="short"
                    >
                      {fund.fundName.trim()}
                    </Heading>
                    <Badge
                      colorScheme={getFundTypeColor(fund.fundTypeTitle)}
                      fontSize={{ base: "xs", md: "sm" }}
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {fund.fundTypeTitle.trim()}
                    </Badge>
                  </HStack>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    color={subtitleColor}
                  >
                    کد ثبت: {fund.fundRegisterCode} &bull; تاریخ آغاز فعالیت:{" "}
                    {formatDate(fund.startDate)}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={3} flexShrink={0}>
                <Button
                  colorScheme="brand"
                  size={{ base: "md", md: "lg" }}
                  rightIcon={
                    <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                  }
                  onClick={handleInvestClick}
                  isDisabled={isInvestmentDisabled}
                  shadow="lg"
                  _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
                  transition="all 0.3s"
                >
                  سرمایه‌گذاری
                </Button>
                {websiteHref && (
                  <Button
                    variant="outline"
                    colorScheme="brand"
                    size={{ base: "md", md: "lg" }}
                    rightIcon={
                      <Icon verticalAlign="middle" as={FiExternalLink as any} />
                    }
                    as="a"
                    href={websiteHref}
                    target="_blank"
                  >
                    وبسایت صندوق
                  </Button>
                )}
              </HStack>
            </Flex>

            {/* Hero Stats Row */}
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={4}
              w="100%"
              mt={4}
            >
              {/* Box 1 – NAV صدور & NAV ابطال */}
              <Box
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                overflow="hidden"
                shadow="sm"
              >
                <HStack
                  px={5}
                  py={3}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  spacing={2}
                >
                  <Box
                    bg="brand.50"
                    _dark={{ bg: "brand.900" }}
                    borderRadius="lg"
                    p={1.5}
                  >
                    <Icon
                      verticalAlign="middle"
                      as={FiDollarSign as any}
                      color="brand.500"
                      boxSize={4}
                    />
                  </Box>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                    ارزش خالص دارایی (NAV)
                  </Text>
                  <Badge
                    colorScheme="brand"
                    variant="subtle"
                    fontSize="2xs"
                    mr="auto"
                  >
                    {formatDate(fund.nav.date)}
                  </Badge>
                </HStack>
                <Grid templateColumns="1fr 1fr">
                  <VStack
                    spacing={0.5}
                    py={5}
                    px={4}
                    align="center"
                    borderRight="1px solid"
                    borderColor={borderColor}
                  >
                    <Text
                      fontSize="2xs"
                      color={subtitleColor}
                      letterSpacing="wide"
                    >
                      {subscriptionNavLabel}
                    </Text>
                    <Text
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="extrabold"
                      color={textColor}
                    >
                      {formatNumber(fund.nav.subscriptionNav)}
                    </Text>
                    <Text fontSize="2xs" color={subtitleColor}>
                      ریال
                    </Text>
                  </VStack>
                  <VStack spacing={0.5} py={5} px={4} align="center">
                    <Text
                      fontSize="2xs"
                      color={subtitleColor}
                      letterSpacing="wide"
                    >
                      {redemptionNavLabel}
                    </Text>
                    <Text
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="extrabold"
                      color={textColor}
                    >
                      {formatNumber(fund.nav.cancelNav)}
                    </Text>
                    <Text fontSize="2xs" color={subtitleColor}>
                      ریال
                    </Text>
                  </VStack>
                </Grid>
              </Box>

              {/* Box 2 – ارزش خالص دارایی & سرمایه‌گذاران */}
              <Box
                bg={cardBg}
                borderRadius="2xl"
                border="1px solid"
                borderColor={borderColor}
                overflow="hidden"
                shadow="sm"
              >
                <HStack
                  px={5}
                  py={3}
                  borderBottom="1px solid"
                  borderColor={borderColor}
                  spacing={2}
                >
                  <Box
                    bg="brand.50"
                    _dark={{ bg: "brand.900" }}
                    borderRadius="lg"
                    p={1.5}
                  >
                    <Icon
                      verticalAlign="middle"
                      as={FiBarChart2 as any}
                      color="brand.500"
                      boxSize={4}
                    />
                  </Box>
                  <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                    حجم صندوق
                  </Text>
                </HStack>
                <Grid templateColumns="1fr 1fr">
                  <VStack
                    spacing={0.5}
                    py={5}
                    px={4}
                    align="center"
                    borderRight="1px solid"
                    borderColor={borderColor}
                  >
                    <Text
                      fontSize="2xs"
                      color={subtitleColor}
                      letterSpacing="wide"
                    >
                      ارزش خالص دارایی
                    </Text>
                    <Text
                      fontSize={{ base: "lg", md: "xl" }}
                      fontWeight="extrabold"
                      color={textColor}
                    >
                      {formatCurrency(fund.nav.totalNetAssetValue)}
                    </Text>
                    <Text fontSize="2xs" color={subtitleColor}>
                      ریال
                    </Text>
                  </VStack>
                  <VStack spacing={0.5} py={5} px={4} align="center">
                    <Text
                      fontSize="2xs"
                      color={subtitleColor}
                      letterSpacing="wide"
                    >
                      سرمایه‌گذاران
                    </Text>
                    <HStack spacing={1}>
                      <Icon
                        verticalAlign="middle"
                        as={FiUsers as any}
                        color="brand.500"
                        boxSize={5}
                      />
                      <Text
                        fontSize={{ base: "lg", md: "xl" }}
                        fontWeight="extrabold"
                        color={textColor}
                      >
                        {formatNumber(fund.nav.totalInvestor)}
                      </Text>
                    </HStack>
                    <Text fontSize="2xs" color={subtitleColor}>
                      نفر
                    </Text>
                  </VStack>
                </Grid>
              </Box>
            </SimpleGrid>
          </MotionVStack>
        </Container>
      </Box>

      {/* ─── RETURNS SECTION ─── */}
      <Box py={{ base: 10, md: 16 }} bg={sectionBg}>
        <Container maxW="container.xl">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <HStack spacing={2} mb={6}>
              <Icon
                verticalAlign="middle"
                as={FiBarChart2 as any}
                color="brand.500"
                boxSize={5}
              />
              <Heading
                as="h2"
                fontSize={{ base: "lg", md: "xl" }}
                color={textColor}
              >
                عملکرد بازدهی
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 7 }} spacing={4}>
              {returnPeriods.map((period) => {
                const isPositive = period.value > 0;
                const accentColor = isPositive ? "green.400" : "red.400";
                const valueBg = isPositive
                  ? returnPositiveBg
                  : returnNegativeBg;
                return (
                  <Box
                    key={period.label}
                    bg={cardBg}
                    borderRadius="xl"
                    overflow="hidden"
                    border="1px solid"
                    borderColor={borderColor}
                    textAlign="center"
                    _hover={{
                      shadow: "lg",
                      transform: "translateY(-3px)",
                      borderColor: isPositive ? "green.300" : "red.300",
                    }}
                    transition="all 0.25s"
                  >
                    {/* Top accent bar */}
                    <Box h="3px" bg={accentColor} />

                    <VStack spacing={2} px={3} pt={4} pb={4}>
                      {/* Period label */}
                      <Text
                        fontSize="2xs"
                        fontWeight="semibold"
                        color={subtitleColor}
                        letterSpacing="wider"
                        textTransform="uppercase"
                      >
                        {period.label}
                      </Text>

                      {/* Value bubble */}
                      <Box
                        bg={valueBg}
                        borderRadius="lg"
                        px={3}
                        py={2}
                        w="100%"
                      >
                        <Text
                          fontSize={{ base: "lg", md: "xl" }}
                          fontWeight="extrabold"
                          color={isPositive ? "green.500" : "red.500"}
                          lineHeight="1"
                        >
                          {isPositive ? "+" : ""}
                          {formatPercent(period.value)}%
                        </Text>
                      </Box>

                      {/* Trend icon */}
                      <Icon
                        verticalAlign="middle"
                        as={(isPositive ? FiTrendingUp : FiTrendingDown) as any}
                        color={isPositive ? "green.400" : "red.400"}
                        boxSize={4}
                        opacity={0.7}
                      />
                    </VStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          </MotionBox>
        </Container>
      </Box>

      {/* ─── FUND INFO + NAV DETAILS ─── */}
      <Box py={{ base: 10, md: 16 }} bg={bgColor}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            {/* Fund Information */}
            <MotionBox
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <GridItem>
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  p={6}
                  border="1px solid"
                  borderColor={borderColor}
                  h="100%"
                >
                  <HStack spacing={2} mb={5}>
                    <Icon
                      verticalAlign="middle"
                      as={FiInfo as any}
                      color="brand.500"
                      boxSize={5}
                    />
                    <Heading as="h3" fontSize="lg" color={textColor}>
                      اطلاعات صندوق
                    </Heading>
                  </HStack>

                  <VStack spacing={4} align="stretch">
                    <InfoRow
                      icon={FiUser}
                      label="مدیر صندوق"
                      value={fund.managerName}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiUser}
                      label="مدیر سرمایه‌گذاری"
                      value={fund.investmentManager.trim() || "ندارد"}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiBookOpen}
                      label="حسابرس"
                      value={fund.auditor}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiShield}
                      label="متولی"
                      value={fund.custodian}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiAward}
                      label="مدیر ثبت"
                      value={fund.registrationManager}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiShield}
                      label="ضامن نقدشوندگی"
                      value={fund.liquidityGurantor1}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    {fund.liquidityGurantor2 && (
                      <InfoRow
                        icon={FiShield}
                        label="ضامن نقدشوندگی ۲"
                        value={fund.liquidityGurantor2}
                        textColor={textColor}
                        subtitleColor={subtitleColor}
                      />
                    )}
                    {fund.profitGurantor && fund.profitGurantor !== "" && (
                      <InfoRow
                        icon={FiShield}
                        label="ضامن سود"
                        value={fund.profitGurantor}
                        textColor={textColor}
                        subtitleColor={subtitleColor}
                      />
                    )}
                    <InfoRow
                      icon={FiGlobe}
                      label="وبسایت"
                      value={fund.websiteAddress}
                      isLink
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                    <InfoRow
                      icon={FiCalendar}
                      label="تاریخ انتشار"
                      value={formatDate(fund.publishDate)}
                      textColor={textColor}
                      subtitleColor={subtitleColor}
                    />
                  </VStack>
                </Box>
              </GridItem>
            </MotionBox>

            {/* NAV & Unit Details */}
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <GridItem>
                <VStack spacing={6} h="100%">
                  {/* NAV Card */}
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor={borderColor}
                    w="100%"
                  >
                    <HStack spacing={2} mb={5}>
                      <Icon
                        verticalAlign="middle"
                        as={FiDollarSign as any}
                        color="brand.500"
                        boxSize={5}
                      />
                      <Heading as="h3" fontSize="lg" color={textColor}>
                        اطلاعات NAV
                      </Heading>
                      <Badge colorScheme="brand" variant="subtle" fontSize="xs">
                        {formatDate(fund.nav.date)}
                      </Badge>
                    </HStack>

                    <SimpleGrid columns={2} spacing={4}>
                      <StatBox
                        label={subscriptionNavLabel}
                        value={`${formatNumber(fund.nav.subscriptionNav)} ریال`}
                        bg={statBg}
                      />
                      <StatBox
                        label={redemptionNavLabel}
                        value={`${formatNumber(fund.nav.cancelNav)} ریال`}
                        bg={statBg}
                      />
                      <StatBox
                        label="NAV آماری"
                        value={`${formatNumber(fund.nav.statisticNav)} ریال`}
                        bg={statBg}
                      />
                      <StatBox
                        label="کل واحدها"
                        value={formatNumber(fund.nav.totalUnit)}
                        bg={statBg}
                      />
                      <StatBox
                        label="واحد صدور امروز"
                        value={formatNumber(fund.nav.totalSubscriptionUnit)}
                        bg={statBg}
                      />
                      <StatBox
                        label="واحد ابطال امروز"
                        value={formatNumber(fund.nav.totalCancelUnit)}
                        bg={statBg}
                      />
                    </SimpleGrid>
                  </Box>

                  {/* Unit Limits Card */}
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor={borderColor}
                    w="100%"
                  >
                    <HStack spacing={2} mb={5}>
                      <Icon
                        verticalAlign="middle"
                        as={FiLayers as any}
                        color="brand.500"
                        boxSize={5}
                      />
                      <Heading as="h3" fontSize="lg" color={textColor}>
                        محدودیت واحدها
                      </Heading>
                    </HStack>

                    <SimpleGrid columns={2} spacing={4}>
                      <StatBox
                        label="حداقل واحد سرمایه‌گذاری"
                        value={formatNumber(fund.minimumCapitalUnit)}
                        bg={statBg}
                      />
                      <StatBox
                        label="حداکثر واحد سرمایه‌گذاری"
                        value={formatNumber(fund.maximumCapitalUnit)}
                        bg={statBg}
                      />
                      <StatBox
                        label="واحد باقیمانده"
                        value={formatNumber(fund.remainedUnit)}
                        bg={statBg}
                      />
                      {fund.profitDay > 0 && (
                        <StatBox
                          label="روز تقسیم سود"
                          value={`${fund.profitDay} هر ماه`}
                          bg={statBg}
                        />
                      )}
                    </SimpleGrid>

                    {fund.guaranteeRate > 0 && (
                      <Box
                        mt={4}
                        bg="green.50"
                        _dark={{ bg: "green.900" }}
                        borderRadius="xl"
                        p={4}
                      >
                        <HStack>
                          <Icon
                            verticalAlign="middle"
                            as={FiCheckCircle as any}
                            color="green.500"
                            boxSize={5}
                          />
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="bold"
                              color="green.600"
                              _dark={{ color: "green.300" }}
                              fontSize="sm"
                            >
                              تضمین سود
                            </Text>
                            <Text
                              fontSize="xs"
                              color="green.600"
                              _dark={{ color: "green.300" }}
                            >
                              نرخ تضمین: {fund.guaranteeRate}%
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    )}
                  </Box>
                </VStack>
              </GridItem>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* ─── WAGES & PENALTIES ─── */}
      <Box py={{ base: 10, md: 16 }} bg={sectionBg}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            {/* Wages */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                h="100%"
              >
                <HStack spacing={2} mb={5}>
                  <Icon
                    verticalAlign="middle"
                    as={FiPercent as any}
                    color="brand.500"
                    boxSize={5}
                  />
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    کارمزدها
                  </Heading>
                </HStack>

                <SimpleGrid columns={2} spacing={4}>
                  <Box bg={statBg} borderRadius="xl" p={4}>
                    <Text fontSize="xs" color={subtitleColor} mb={1}>
                      کارمزد ثابت صدور
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                      {Number(issuanceFixedWage) > 0
                        ? `${formatNumber(Number(issuanceFixedWage))} ریال`
                        : "رایگان"}
                    </Text>
                  </Box>
                  <Box bg={statBg} borderRadius="xl" p={4}>
                    <Text fontSize="xs" color={subtitleColor} mb={1}>
                      کارمزد ثابت ابطال
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                      {Number(redemptionFixedWage) > 0
                        ? `${formatNumber(Number(redemptionFixedWage))} ریال`
                        : "رایگان"}
                    </Text>
                  </Box>
                  <Box bg={statBg} borderRadius="xl" p={4}>
                    <Text fontSize="xs" color={subtitleColor} mb={1}>
                      نرخ کارمزد متغیر صدور
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                      {Number(issuanceVarRate) > 0
                        ? `${issuanceVarRate}%`
                        : "ندارد"}
                    </Text>
                  </Box>
                  <Box bg={statBg} borderRadius="xl" p={4}>
                    <Text fontSize="xs" color={subtitleColor} mb={1}>
                      سقف کارمزد متغیر
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color={textColor}>
                      {Number(issuanceMaxVar) > 0
                        ? `${formatNumber(Number(issuanceMaxVar))} ریال`
                        : "ندارد"}
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </MotionBox>

            {/* Penalties */}
            {/* <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                h="100%"
              >
                <HStack spacing={2} mb={5}>
                  <Icon
                    verticalAlign="middle"
                    as={FiAlertTriangle as any}
                    color="orange.500"
                    boxSize={5}
                  />
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    جریمه ابطال زودهنگام
                  </Heading>
                </HStack>

                {penalties.length > 0 ? (
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th color={subtitleColor} fontSize="xs">
                            مرحله
                          </Th>
                          <Th color={subtitleColor} fontSize="xs">
                            تا روز
                          </Th>
                          <Th color={subtitleColor} fontSize="xs" isNumeric>
                            درصد جریمه
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {penalties.map((p, idx) => (
                          <Tr key={idx}>
                            <Td>
                              <Badge
                                colorScheme="orange"
                                variant="subtle"
                                fontSize="xs"
                              >
                                مرحله {idx + 1}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontWeight="medium" color={textColor}>
                                {p.days} روز
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontWeight="bold" color="orange.500">
                                {p.percent}%
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Box
                    bg="green.50"
                    _dark={{ bg: "green.900" }}
                    borderRadius="xl"
                    p={4}
                    textAlign="center"
                  >
                    <HStack justify="center" spacing={2}>
                      <Icon
                        verticalAlign="middle"
                        as={FiCheckCircle as any}
                        color="green.500"
                      />
                      <Text
                        color="green.600"
                        _dark={{ color: "green.300" }}
                        fontWeight="medium"
                      >
                        بدون جریمه ابطال زودهنگام
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </MotionBox> */}
          </Grid>
        </Container>
      </Box>

      {/* ─── RISK LEVEL & ASSET COMPOSITION ─── */}
      <Box py={{ base: 10, md: 16 }} bg={bgColor}>
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8}>
            {/* Risk Level */}
            <MotionBox
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                h="100%"
              >
                <HStack spacing={2} mb={5}>
                  <Icon
                    verticalAlign="middle"
                    as={FiActivity as any}
                    color="brand.500"
                    boxSize={5}
                  />
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    سطح ریسک صندوق
                  </Heading>
                </HStack>

                <VStack spacing={6} align="center" py={4}>
                  <CircularProgress
                    value={riskScore}
                    size="160px"
                    thickness="10px"
                    color={
                      riskScore <= 30
                        ? "green.400"
                        : riskScore <= 60
                          ? "yellow.400"
                          : "red.400"
                    }
                    trackColor={progressTrackColor}
                  >
                    <CircularProgressLabel>
                      <VStack spacing={0}>
                        <Text
                          fontSize="2xl"
                          fontWeight="extrabold"
                          color={textColor}
                        >
                          {riskScore}
                        </Text>
                        <Text fontSize="2xs" color={subtitleColor}>
                          از ۱۰۰
                        </Text>
                      </VStack>
                    </CircularProgressLabel>
                  </CircularProgress>

                  <Badge
                    colorScheme={
                      riskScore <= 30
                        ? "green"
                        : riskScore <= 60
                          ? "yellow"
                          : "red"
                    }
                    fontSize="sm"
                    px={4}
                    py={1.5}
                    borderRadius="full"
                  >
                    {riskScore <= 30
                      ? "کم‌ریسک"
                      : riskScore <= 60
                        ? "ریسک متوسط"
                        : "پرریسک"}
                  </Badge>

                  <Text
                    fontSize="sm"
                    color={subtitleColor}
                    textAlign="center"
                    maxW="300px"
                  >
                    {riskScore <= 30
                      ? "این صندوق برای سرمایه‌گذاران محافظه‌کار و ریسک‌گریز مناسب است."
                      : riskScore <= 60
                        ? "این صندوق برای سرمایه‌گذاران با ریسک‌پذیری متوسط مناسب است."
                        : "این صندوق برای سرمایه‌گذاران ریسک‌پذیر با افق بلندمدت مناسب است."}
                  </Text>

                  {/* Risk bar */}
                  <Box w="100%">
                    <Flex justify="space-between" mb={1}>
                      <Text fontSize="2xs" color="green.500">
                        کم‌ریسک
                      </Text>
                      <Text fontSize="2xs" color="red.500">
                        پرریسک
                      </Text>
                    </Flex>
                    <Progress
                      value={riskScore}
                      size="sm"
                      borderRadius="full"
                      colorScheme={
                        riskScore <= 30
                          ? "green"
                          : riskScore <= 60
                            ? "yellow"
                            : "red"
                      }
                      bg={progressTrackColor}
                    />
                  </Box>
                </VStack>
              </Box>
            </MotionBox>

            {/* Asset Composition */}
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
                h="100%"
              >
                <HStack spacing={2} mb={5}>
                  <Icon
                    verticalAlign="middle"
                    as={FiPieChart as any}
                    color="brand.500"
                    boxSize={5}
                  />
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    ترکیب دارایی صندوق
                  </Heading>
                </HStack>

                <Flex
                  direction={{ base: "column", md: "row" }}
                  align="center"
                  gap={6}
                >
                  <Box
                    w={{ base: "200px", md: "220px" }}
                    h={{ base: "200px", md: "220px" }}
                  >
                    <Doughnut
                      data={assetCompositionData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: true,
                        cutout: "60%",
                        plugins: {
                          legend: { display: false },
                        },
                      }}
                    />
                  </Box>
                  <VStack spacing={3} align="start" flex={1}>
                    {assetCompositionItems.map((item, idx) => (
                      <HStack key={idx} spacing={3} w="100%">
                        <Box
                          w={3}
                          h={3}
                          borderRadius="full"
                          bg={item.color}
                          flexShrink={0}
                        />
                        <Text fontSize="sm" color={textColor} flex={1}>
                          {item.label}
                        </Text>
                        <Text fontSize="sm" fontWeight="bold" color={textColor}>
                          {item.value}%
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Flex>
              </Box>
            </MotionBox>
          </Grid>
        </Container>
      </Box>

      {/* ─── HOW TO INVEST ─── */}
      <Box py={{ base: 10, md: 16 }} bg={sectionBg}>
        <Container maxW="container.xl">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <HStack spacing={2} mb={8}>
              <Icon
                verticalAlign="middle"
                as={FiLogIn as any}
                color="brand.500"
                boxSize={5}
              />
              <Heading
                as="h2"
                fontSize={{ base: "lg", md: "xl" }}
                color={textColor}
              >
                چگونه سرمایه‌گذاری کنم؟
              </Heading>
            </HStack>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
              {investSteps.map((step, idx) => (
                <MotionBox
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                >
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    p={6}
                    border="1px solid"
                    borderColor={borderColor}
                    textAlign="center"
                    position="relative"
                    _hover={{
                      shadow: "lg",
                      transform: "translateY(-4px)",
                      borderColor: "brand.300",
                    }}
                    transition="all 0.3s"
                    h="100%"
                  >
                    {/* Step number badge */}
                    <Box
                      position="absolute"
                      top={-3}
                      right={-3}
                      bg="brand.500"
                      color="white"
                      w={7}
                      h={7}
                      borderRadius="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                      fontWeight="bold"
                      shadow="md"
                    >
                      {idx + 1}
                    </Box>

                    <VStack spacing={4}>
                      <Box
                        bg="brand.50"
                        _dark={{ bg: "brand.900" }}
                        p={4}
                        borderRadius="xl"
                      >
                        <Icon
                          verticalAlign="middle"
                          as={step.icon as any}
                          boxSize={8}
                          color="brand.500"
                        />
                      </Box>
                      <Heading as="h4" fontSize="md" color={textColor}>
                        {step.title}
                      </Heading>
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        lineHeight="tall"
                      >
                        {step.description}
                      </Text>
                    </VStack>
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>

            <Flex justify="center" mt={8}>
              <Button
                colorScheme="brand"
                size="lg"
                rightIcon={
                  <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                }
                onClick={handleInvestClick}
                isDisabled={isInvestmentDisabled}
                shadow="lg"
                _hover={{
                  transform: "translateY(-2px)",
                  shadow: "xl",
                }}
                transition="all 0.3s"
              >
                شروع سرمایه‌گذاری
              </Button>
            </Flex>
          </MotionBox>
        </Container>
      </Box>

      {/* ─── FAQ ─── */}
      <Box py={{ base: 10, md: 16 }} bg={bgColor}>
        <Container maxW="container.xl">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <HStack spacing={2} mb={8}>
              <Icon
                verticalAlign="middle"
                as={FiHelpCircle as any}
                color="brand.500"
                boxSize={5}
              />
              <Heading
                as="h2"
                fontSize={{ base: "lg", md: "xl" }}
                color={textColor}
              >
                سوالات متداول
              </Heading>
            </HStack>

            <Box
              bg={cardBg}
              borderRadius="2xl"
              border="1px solid"
              borderColor={borderColor}
              overflow="hidden"
            >
              <Accordion allowToggle>
                {faqItems.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    border="none"
                    borderBottom={
                      idx < faqItems.length - 1 ? "1px solid" : "none"
                    }
                    borderColor={borderColor}
                  >
                    <AccordionButton
                      py={5}
                      px={6}
                      _hover={{ bg: statBg }}
                      _expanded={{
                        bg: statBg,
                        borderBottom: "1px solid",
                        borderColor: borderColor,
                      }}
                    >
                      <HStack flex={1} spacing={3} textAlign="right">
                        <Icon
                          verticalAlign="middle"
                          as={FiHelpCircle as any}
                          color="brand.500"
                          boxSize={4}
                          flexShrink={0}
                        />
                        <Text
                          fontWeight="semibold"
                          color={textColor}
                          fontSize="sm"
                        >
                          {faq.question}
                        </Text>
                      </HStack>
                      <AccordionIcon color={subtitleColor} />
                    </AccordionButton>
                    <AccordionPanel px={6} py={4}>
                      <Text
                        fontSize="sm"
                        color={subtitleColor}
                        lineHeight="tall"
                      >
                        {faq.answer}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>
          </MotionBox>
        </Container>
      </Box>

      {/* ─── NAV Adjust History ─── */}
      {/* {fund.navAdjustSetting.length > 0 && (
        <Box py={{ base: 10, md: 16 }} bg={bgColor}>
          <Container maxW="container.xl">
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Box
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                border="1px solid"
                borderColor={borderColor}
              >
                <HStack spacing={2} mb={5}>
                  <Icon
                    verticalAlign="middle"
                    as={FiLayers as any}
                    color="brand.500"
                    boxSize={5}
                  />
                  <Heading as="h3" fontSize="lg" color={textColor}>
                    تاریخچه تعدیل NAV
                  </Heading>
                </HStack>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                  {fund.navAdjustSetting.map((adj, idx) => (
                    <Box
                      key={idx}
                      bg={statBg}
                      borderRadius="xl"
                      p={4}
                      border="1px solid"
                      borderColor={borderColor}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xs" color={subtitleColor}>
                            نرخ تقسیم
                          </Text>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="brand.500"
                          >
                            ۱:{adj.splitRate}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Text fontSize="xs" color={subtitleColor}>
                            تاریخ
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color={textColor}
                          >
                            {formatDate(adj.rateDate)}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </MotionBox>
          </Container>
        </Box>
      )} */}

      {/* ─── Bottom CTA ─── */}
      <Box py={{ base: 12, md: 16 }} bg={sectionBg}>
        <Container maxW="container.md">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Box
              bgGradient={ctaBgGradient}
              borderRadius="2xl"
              p={{ base: 8, md: 12 }}
              textAlign="center"
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: "absolute",
                top: "-50%",
                right: "-20%",
                width: "300px",
                height: "300px",
                borderRadius: "full",
                bg: "whiteAlpha.100",
              }}
              _after={{
                content: '""',
                position: "absolute",
                bottom: "-30%",
                left: "-10%",
                width: "200px",
                height: "200px",
                borderRadius: "full",
                bg: "whiteAlpha.100",
              }}
            >
              <VStack spacing={4} position="relative" zIndex={1}>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  color="white"
                  fontWeight="bold"
                >
                  همین الان سرمایه‌گذاری را شروع کنید
                </Heading>
                <Text
                  color="whiteAlpha.800"
                  fontSize={{ base: "sm", md: "md" }}
                  maxW="500px"
                >
                  با سرمایه‌گذاری در {fund.fundName.trim()} از بازدهی{" "}
                  {formatPercent(fund.annualReturn)}% سالانه بهره‌مند شوید
                </Text>
                <HStack spacing={4} pt={2}>
                  <Button
                    bg="white"
                    color="brand.600"
                    size="lg"
                    rightIcon={
                      <Icon verticalAlign="middle" as={FiArrowLeft as any} />
                    }
                    onClick={handleInvestClick}
                    isDisabled={isInvestmentDisabled}
                    _hover={{
                      bg: "whiteAlpha.900",
                      transform: "translateY(-2px)",
                      shadow: "xl",
                    }}
                    transition="all 0.3s"
                    fontWeight="bold"
                  >
                    سرمایه‌گذاری
                  </Button>
                  <Button
                    variant="outline"
                    borderColor="whiteAlpha.500"
                    color="white"
                    size="lg"
                    onClick={() => navigate("/funds")}
                    _hover={{
                      bg: "whiteAlpha.200",
                      transform: "translateY(-2px)",
                    }}
                    transition="all 0.3s"
                  >
                    سایر صندوق‌ها
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </MotionBox>
        </Container>
      </Box>
    </>
  );
};

// ─── Sub-components ────────────────────────────────────────

const InfoRow = ({
  icon,
  label,
  value,
  isLink,
  textColor,
  subtitleColor,
}: {
  icon: any;
  label: string;
  value: string;
  isLink?: boolean;
  textColor: string;
  subtitleColor: string;
}) => {
  const rowBorderColor = useColorModeValue("gray.100", "gray.700");
  return (
    <Flex
      justify="space-between"
      align="center"
      py={2}
      borderBottom="1px solid"
      borderColor={rowBorderColor}
      _last={{ borderBottom: "none" }}
    >
      <HStack spacing={2}>
        <Icon
          verticalAlign="middle"
          as={icon as any}
          color="brand.500"
          boxSize={4}
        />
        <Text fontSize="sm" color={subtitleColor}>
          {label}
        </Text>
      </HStack>
      {isLink ? (
        <Link
          href={`https://${value}`}
          isExternal
          color="brand.500"
          fontSize="sm"
          fontWeight="medium"
          _hover={{ textDecoration: "underline" }}
        >
          {value}
        </Link>
      ) : (
        <Text
          fontSize="sm"
          fontWeight="medium"
          color={textColor}
          textAlign="left"
        >
          {value}
        </Text>
      )}
    </Flex>
  );
};

const StatBox = ({
  label,
  value,
  bg,
}: {
  label: string;
  value: string;
  bg: string;
}) => {
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valColor = useColorModeValue("gray.800", "white");
  return (
    <Box bg={bg} borderRadius="xl" p={3} textAlign="center">
      <Text fontSize="2xs" color={labelColor} mb={1}>
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="bold" color={valColor}>
        {value}
      </Text>
    </Box>
  );
};
